import {
  hostname,
  ProposalReview,
  truncate,
  WebsiteProposalPanel,
} from "@/components/molecules/website-proposal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import {
  useCompanyProposal,
  useDismissCompanyProposal,
  useStartCompanyProposal,
} from "@/features/onboarding/hooks/use-company-proposal";
import type { CompanyProposal } from "@/features/onboarding/types";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowsClockwiseIcon, SparkleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

type Field = keyof CompanyProposal;

/** Form order, so the review reads like the form underneath it. */
const FIELDS: Field[] = ["name", "about", "address"];

/**
 * "Remplir depuis mon site" — the wizard's website analysis, offered at any
 * time from the company settings.
 *
 * It proposes, it never saves: the owner ticks what to keep, « Appliquer »
 * fills the form fields below, and the usual « Enregistrer » is what writes
 * the company. Reads the website *as the form holds it*, so an address typed a
 * second ago works without saving it first.
 */
export function WebsiteProposal({
  website,
  current,
  onApply,
}: {
  website: string;
  current: Required<CompanyProposal>;
  onApply: (values: CompanyProposal) => void;
}) {
  const { t } = useTranslation("companySettings");
  const { data: state } = useCompanyProposal();
  const startRun = useStartCompanyProposal();
  const dismiss = useDismissCompanyProposal();

  const site = website.trim();
  const isRunning = state?.status === "running" || startRun.isPending;
  const proposal = state?.status === "done" ? state.proposal : null;

  const handleStart = async () => {
    try {
      await startRun.mutateAsync({ url: site });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("companySettings.fromWebsite.startError"),
      );
    }
  };

  const handleDismiss = () => dismiss.mutate();

  const handleApply = (values: CompanyProposal) => {
    onApply(values);
    dismiss.mutate();
    toast.success(t("companySettings.fromWebsite.applied"));
  };

  return (
    <WebsiteProposalPanel
      title={t("companySettings.fromWebsite.title")}
      description={
        !site
          ? t("companySettings.fromWebsite.noWebsite")
          : isRunning
            ? t("companySettings.fromWebsite.running", {
                website: hostname(state?.url || site),
              })
            : t("companySettings.fromWebsite.description", {
                website: hostname(site),
              })
      }
      // The server's reason is technical and in English: it is in the logs,
      // the owner only needs to know nothing changed.
      error={
        !isRunning && state?.status === "failed"
          ? t("companySettings.fromWebsite.failed")
          : null
      }
      action={
        <Button
          type="button"
          variant={proposal ? "outline" : "default"}
          onClick={handleStart}
          disabled={!site || isRunning}
        >
          {isRunning ? (
            <>
              <Spinner className="h-4 w-4" />
              {t("companySettings.fromWebsite.actions.running")}
            </>
          ) : proposal || state?.status === "failed" ? (
            <>
              <ArrowsClockwiseIcon className="h-4 w-4" weight="bold" />
              {t("companySettings.fromWebsite.actions.again")}
            </>
          ) : (
            <>
              <SparkleIcon className="h-4 w-4" weight="fill" />
              {t("companySettings.fromWebsite.actions.start")}
            </>
          )}
        </Button>
      }
    >
      {proposal && !isRunning ? (
        <CompanyProposalReview
          // A new run is a new proposal: start its selection from scratch.
          key={state?.started_at || 0}
          proposal={proposal}
          current={current}
          onApply={handleApply}
          onDismiss={handleDismiss}
        />
      ) : null}
    </WebsiteProposalPanel>
  );
}

/**
 * What the site would change, field by field, next to what the form holds
 * now. A field the site agrees with, or said nothing about, is not listed.
 */
function CompanyProposalReview({
  proposal,
  current,
  onApply,
  onDismiss,
}: {
  proposal: CompanyProposal;
  current: Required<CompanyProposal>;
  onApply: (values: CompanyProposal) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation("companySettings");

  const changes = FIELDS.filter((field) => {
    const value = (proposal[field] || "").trim();
    return value && value !== (current[field] || "").trim();
  });

  return (
    <ProposalReview
      intro={t("companySettings.fromWebsite.review")}
      nothingNew={t("companySettings.fromWebsite.nothingNew")}
      hint={t("companySettings.fromWebsite.applyHint")}
      applyLabel={t("companySettings.fromWebsite.actions.apply")}
      items={changes.map((field) => ({
        key: field,
        label: t(`companySettings.general.${FIELD_LABELS[field]}`),
        value: proposal[field],
        note: current[field]?.trim()
          ? t("companySettings.fromWebsite.replaces", {
              value: truncate(current[field]),
            })
          : t("companySettings.fromWebsite.currentlyEmpty"),
      }))}
      onApply={(choices) =>
        onApply(
          Object.fromEntries(
            choices.map(({ key }) => [key, proposal[key as Field]]),
          ) as CompanyProposal,
        )
      }
      onDismiss={onDismiss}
    />
  );
}

/** The labels the form itself uses, so the review names fields the same way. */
const FIELD_LABELS: Record<Field, string> = {
  name: "companyName",
  about: "about",
  address: "address",
};
